/**
 * The e-mail signature itself.
 *
 * Shared by the creation screen and by the list, and that sharing is the point: the PNG that gets
 * e-mailed is this element captured with `toPng`, so anything that renders it differently would
 * deliver something different from what the person saw.
 *
 * **Every colour here is a literal, never a theme token.** What this renders leaves the app and
 * lands in someone's mail client, where the panel's theme means nothing: a token would put the
 * dark palette into the image of anyone who generated a signature with dark mode on, and a
 * signature that is a dark rectangle in an e-mail is not recoverable after the fact.
 *
 * It draws no border and no shadow for the same reason — a frame around the signature would be
 * baked into the PNG. Screens that want to set it apart draw that around the element, not on it.
 *
 * @module Pages/Signatures
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import type { JSX } from 'react';
import { BU_CONFIGS } from '../SignatureCreate/SignatureCreate.types';

/** One template for every BU; the BU only decides where the record is stored. */
const TEMPLATE = BU_CONFIGS.group_link_one;

const cardStyle = css`
  background: #ffffff;
  padding: var(--space-ml);
  display: flex;
  gap: var(--space-ml);
  align-items: flex-start;
`;

const sealStyle = css`
  flex: 0 0 auto;
  display: block;
  width: 74px;
  height: 74px;
  border-radius: var(--radius-m);
`;

const textStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const nameStyle = css`
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  line-height: 1.25;
  color: #313337;
`;

const roleStyle = css`
  font-size: 14px;
  font-weight: var(--font-weight-semibold);
  line-height: 1.3;
  color: #58616a;
`;

const gapStyle = css`
  height: 10px;
`;

const lineStyle = css`
  font-size: var(--font-size-sm);
  color: #58616a;
`;

const taglineStyle = css`
  font-size: var(--font-size-xs);
  color: #888e9e;
  text-wrap: pretty;
`;

const socialsStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-s);
  padding-top: 6px;
`;

const socialStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: var(--font-size-xs);
  color: #58616a;
`;

export interface SignaturePreviewProps {
  name: string;
  position?: string | null;
  phone?: string | null;
  email: string;
  /** Set on the element `toPng` captures. */
  id?: string;
  /** Show the placeholder text while the form is still empty. */
  withPlaceholders?: boolean;
}

export const SignaturePreview = ({
  name,
  position,
  phone,
  email,
  id,
  withPlaceholders = false,
}: SignaturePreviewProps): JSX.Element => (
  <div id={id} className={cardStyle}>
    <img className={sealStyle} src={TEMPLATE.signatureLogo} alt='GroupLink One' />
    <div className={textStyle}>
      <span className={nameStyle}>{name || (withPlaceholders ? 'Nome do funcionário' : '')}</span>
      <span className={roleStyle}>{position || (withPlaceholders ? 'Cargo' : '')}</span>
      <span className={gapStyle} />
      <span className={lineStyle}>{phone || (withPlaceholders ? '+55 11 00000-0000' : '')}</span>
      <span className={lineStyle}>
        {email || (withPlaceholders ? 'nome@grouplinkone.com' : '')}
      </span>
      <span className={lineStyle}>{TEMPLATE.website}</span>
      <span className={gapStyle} />
      <span className={taglineStyle}>{TEMPLATE.tagline}</span>
      <div className={socialsStyle}>
        <span className={socialStyle}>
          <GLIcon variant='Social.LinkedinLogo' size={14} fill='#0033ff' />
          {TEMPLATE.socialMedia.linkedin}
        </span>
        <span className={socialStyle}>
          <GLIcon variant='Social.InstagramLogo' size={14} fill='#0033ff' />
          {TEMPLATE.socialMedia.instagram}
        </span>
        <span className={socialStyle}>
          <GLIcon variant='Social.YoutubeLogo' size={14} fill='#0033ff' />
          {TEMPLATE.socialMedia.youtube}
        </span>
      </div>
    </div>
  </div>
);
